import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Clock, Zap, TrendingUp, X } from 'lucide-react';
import { Product } from '@/types';
import { formatPrice, truncateText } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { DiscountBadge } from './discount-badge';

interface RecommendedProductCardProps {
  product: Product;
  onClose: () => void;
}

export function RecommendedProductCard({ product, onClose }: RecommendedProductCardProps) {
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const discountInfo = calculateProductDiscount(product, 1);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onClose]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.8, y: 50 }}
        transition={{ type: "spring", duration: 0.6 }}
        className="fixed bottom-4 right-4 z-40 max-w-sm w-full mx-4"
      >
        <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 p-1 rounded-2xl shadow-2xl">
          <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
            {/* Header with close button */}
            <div className="relative">
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={onClose}
                  className="p-1 rounded-full bg-black/20 hover:bg-black/30 text-white transition-colors"
                >
                  <motion.div whileHover={{ rotate: 90 }} transition={{ duration: 0.2 }}>
                    <X className="h-4 w-4" />
                  </motion.div>
                </button>
              </div>

              {/* Trending badge */}
              <div className="absolute top-2 left-2 z-10">
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="flex items-center gap-1 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold"
                >
                  <TrendingUp className="h-3 w-3" />
                  HOT DEAL
                </motion.div>
              </div>

              {/* Product Image */}
              <Link to={`/product/${product.id}`} onClick={onClose}>
                <div className="aspect-square relative overflow-hidden">
                  <img
                    src={product.images[0]}
                    alt={product.produt_name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  
                  {/* Discount badge */}
                  {discountInfo.hasDiscount && (
                    <div className="absolute bottom-2 left-2">
                      <DiscountBadge 
                        discountType={discountInfo.discountType}
                        discountAmount={discountInfo.discountAmount}
                      />
                    </div>
                  )}
                </div>
              </Link>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Timer */}
              <motion.div
                animate={{ 
                  scale: [1, 1.05, 1],
                }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="flex items-center justify-center gap-2 mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
              >
                <Clock className="h-4 w-4 text-red-500" />
                <span className="text-sm font-bold text-red-600 dark:text-red-400">
                  Limited Time: {formatTime(timeLeft)}
                </span>
              </motion.div>

              <Link to={`/product/${product.id}`} onClick={onClose}>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2 hover:text-brand-primary dark:hover:text-brand-light transition-colors line-clamp-2">
                  {truncateText(product.produt_name, 60)}
                </h3>
              </Link>

              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className="h-3 w-3 text-yellow-400 fill-current"
                    />
                  ))}
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                    (4.8)
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                  {product.category}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-lg font-bold text-brand-primary dark:text-brand-light">
                    {formatPrice(product.sellingprice)}
                  </span>
                  {discountInfo.hasDiscount && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Buy {discountInfo.threshold}+ for discount
                    </span>
                  )}
                </div>
                
                <Link to={`/product/${product.id}`} onClick={onClose}>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-brand-primary to-brand-light text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <Zap className="h-4 w-4 mr-1 inline" />
                    View Deal
                  </motion.button>
                </Link>
              </div>
            </div>

            {/* Pulsing border animation */}
            <motion.div
              animate={{
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute inset-0 rounded-2xl border-2 border-yellow-400 pointer-events-none"
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}